import type {
  PersistentExecutionProgram,
  ProgramWorkItemState,
} from "./executionProgramState";

export interface OperationalChecklistItem {
  readonly id: string;

  readonly phaseId: string;

  readonly title: string;

  readonly department: string;

  readonly status: "pending" | "active" | "success" | "error";

  readonly evidence: readonly string[];
}

export interface OperationalTakeoverStatus {
  readonly programId: string;

  readonly objective: string;

  readonly state: string;

  readonly progressPercent: number;

  readonly completed: number;

  readonly active: number;

  readonly failed: number;

  readonly pending: number;

  readonly checklist: readonly OperationalChecklistItem[];
}

function itemStatus(
  item: ProgramWorkItemState,
): OperationalChecklistItem["status"] {
  if (item.state === "GREEN") {
    return "success";
  }

  if (item.state === "FAILED" || item.state === "BLOCKED") {
    return "error";
  }

  if (item.state === "RUNNING" || item.state === "VERIFYING") {
    return "active";
  }

  return "pending";
}

export function createOperationalTakeoverStatus(
  program: PersistentExecutionProgram,
): OperationalTakeoverStatus {
  const checklist = program.workItems.map((item) => ({
    id: item.workItem.id,

    phaseId: item.workItem.phaseId,

    title: item.workItem.objective,

    department: item.workItem.department,

    status: itemStatus(item),

    evidence: item.evidence,
  }));

  const completed = checklist.filter(
    (item) => item.status === "success",
  ).length;

  const active = checklist.filter((item) => item.status === "active").length;

  const failed = checklist.filter((item) => item.status === "error").length;

  const pending = checklist.length - completed - active - failed;

  return {
    programId: program.id,

    objective: program.objective,

    state: program.state,

    progressPercent: checklist.length
      ? Math.round((completed / checklist.length) * 100)
      : 100,

    completed,
    active,
    failed,
    pending,
    checklist,
  };
}
